import styled, { css, keyframes } from 'styled-components';
import breakpoints from 'theme/breakpoints';
import { ui } from 'theme/discover';

/**
 * Peças do cartão de destaque do app (WUIHeroCard) e da faixa que o gira,
 * divididas entre os destaques de eventos e os de lugares: a foto ocupa o
 * cartão, um degradê escuro segura o texto branco por cima.
 */

const fadeInUp = keyframes`
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
`;

export const HeroCarouselWrapper = styled.div`
    position: relative;
    animation: ${fadeInUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

export const HeroCarouselViewport = styled.div`
    width: 100%;
    overflow: hidden;
    position: relative;
`;

/*
 * Realce do slide central: só faz sentido quando os cards giram em torno de um
 * meio. Numa faixa curta, que fica parada, o mesmo efeito deixa o primeiro card
 * maior que os outros, estoura a borda esquerda e apaga o último — o carrossel
 * parece quebrado em vez de hierarquizado.
 */
const centeredEmphasis = css`
    @media screen and (min-width: 1200px) {
        .swiper-slide {
            cursor: grab;
            transform: scale(0.9);
            opacity: 0.6;
        }

        .swiper-slide-active {
            transform: scale(1);
            opacity: 1;
            z-index: 3;
            cursor: pointer;
        }

        .swiper-slide-next,
        .swiper-slide-prev {
            transform: scale(0.95);
            opacity: 0.85;
            z-index: 2;
        }
    }
`;

/** Pele do Swiper: transições dos slides e os pontos em pílula embaixo. */
export const heroSwiperSkin = css`
    width: 100%;
    height: auto;
    padding: 4px 0;

    .swiper-slide {
        transition: transform 0.35s ease, opacity 0.35s ease;
        cursor: pointer;
    }

    ${({ $emphasis }) => $emphasis && centeredEmphasis}

    .swiper-pagination {
        position: relative;
        margin-top: 14px;
        text-align: center;
    }

    .swiper-pagination-bullet {
        background: ${ui.inkFaint};
        opacity: 0.45;
        width: 6px;
        height: 6px;
        margin: 0 4px;
        border-radius: 999px;
        transition: width 0.25s ease, opacity 0.25s ease, background 0.25s ease;
        cursor: pointer;
    }

    .swiper-pagination-bullet-active {
        width: 18px;
        background: ${ui.link};
        opacity: 1;
    }
`;

export const HeroArrow = styled.button`
    position: absolute;
    ${({ $side }) => $side === 'left' ? 'left: 12px;' : 'right: 12px;'}
    top: calc(50% - 14px);
    transform: translateY(-50%);
    z-index: 4;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${ui.ink};
    background: ${ui.overlayButton};
    box-shadow: 0 4px 14px rgba(10, 5, 25, 0.18);
    transition: transform 0.15s ease, color 0.15s ease;

    &:hover {
        color: ${ui.link};
    }

    &:active {
        transform: translateY(-50%) scale(0.94);
    }

    @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
        display: none;
    }
`;

export const HeroCard = styled.div`
    position: relative;
    height: 220px;
    border-radius: 22px;
    overflow: hidden;
    background: ${ui.surfaceSunken};

    & > img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.5s ease;
    }

    &:hover > img {
        transform: scale(1.04);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        height: 240px;
    }

    @media (prefers-reduced-motion: reduce) {
        &:hover > img {
            transform: none;
        }
    }
`;

export const HeroScrim = styled.div`
    position: absolute;
    inset: 0;
    background: ${ui.scrimCard};
    pointer-events: none;
`;

export const HeroCorner = styled.div`
    position: absolute;
    top: 14px;
    ${({ $side }) => $side === 'left' ? 'left: 14px;' : 'right: 14px;'}
    z-index: 2;
`;

// Vidro claro sobre a foto: o selo e o coração dos destaques.
const glass = css`
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.28);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
`;

export const HeroBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 700;
    color: #FFFFFF;
    ${glass}
`;

export const HeroGlassButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    ${glass}
    transition: transform 0.15s ease, background 0.15s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    &:active {
        transform: scale(0.9);
    }

    &:focus-visible {
        outline: 2px solid #FFFFFF;
        outline-offset: 2px;
    }
`;

export const HeroContent = styled.div`
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 14px;
    z-index: 1;
    color: #FFFFFF;
    text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
    pointer-events: none;
`;

export const HeroTitle = styled.strong`
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 18px;
    line-height: 1.25;
    font-weight: 700;
    letter-spacing: -0.01em;
`;

export const HeroSubtitle = styled.span`
    display: block;
    margin-top: 4px;
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.9);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const HeroMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 6px;
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.9);
    min-width: 0;

    & > span {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        min-width: 0;
    }

    svg {
        flex-shrink: 0;
    }
`;

/** Pontos de quebra do Swiper: um cartão e uma ponta do próximo no celular, três no desktop. */
export function heroBreakpoints(centered) {
    return {
        0: { slidesPerView: 1.08, spaceBetween: 12, centeredSlides: false },
        640: { slidesPerView: 2, spaceBetween: 14, centeredSlides: false },
        1200: { slidesPerView: 3, spaceBetween: 14, centeredSlides: centered },
    };
}
